import { useState } from 'react';
import { z } from 'zod';
import type {
  CreateWerfverslagCommand,
  PhaseId,
  UpdateWerfverslagCommand,
  WerfverslagDto,
} from '@/api/generated';
import {
  useBatchCreateTasks,
  useContacts,
  useCreateWerfverslag,
  useUpdateWerfverslag,
} from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import styles from './WerfverslagDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

const schema = z.object({
  date: z.string().min(1, 'Kies een datum'),
  phaseId: z.string().min(1, 'Kies een fase'),
  attendeeContactIds: z.array(z.string()).min(1, 'Selecteer minstens 1 aanwezige'),
  itemsCsv: z.string().optional().or(z.literal('')),
});

type WerfverslagFormValues = z.infer<typeof schema>;

function parseList(raw: string): string[] {
  const parts = raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

/**
 * Multi-select list of the project's contacts. Bound to a form field holding
 * an array of selected contact IDs; toggling a checkbox adds/removes the ID.
 */
function AttendeePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const contacts = useContacts({ pageSize: 100 });
  const items = contacts.data?.items ?? [];
  const selected = new Set(value);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  if (contacts.isLoading) {
    return <div className="mono c-ink-3">Contacten laden…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="mono c-ink-3">
        Nog geen contacten. Voeg eerst contacten toe om aanwezigen te kunnen selecteren.
      </div>
    );
  }

  return (
    <ul className={styles.contactList}>
      {items.map((c) => (
        <li key={c.id} className={styles.contactRow}>
          <label className={styles.contactLabel}>
            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
            <span className={styles.contactName}>{c.name}</span>
            {c.role ? <span className={styles.contactRole}>{c.role}</span> : null}
          </label>
        </li>
      ))}
    </ul>
  );
}

function FieldsBody() {
  return (
    <>
      <FormField<WerfverslagFormValues> name="date" label="Datum vergadering" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="date"
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      </FormField>
      <FormField<WerfverslagFormValues> name="phaseId" label="Fase" required>
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
      <FormField<WerfverslagFormValues>
        name="attendeeContactIds"
        label="Aanwezigen"
        hint="Selecteer de aanwezige contacten"
        required
      >
        {(field) => (
          <AttendeePicker
            value={(field.value as string[]) ?? []}
            onChange={(next) => field.onChange(next)}
          />
        )}
      </FormField>
      <FormField<WerfverslagFormValues>
        name="itemsCsv"
        label="Besproken items"
        hint="Eén item per regel of gescheiden door komma's"
      >
        {(field) => (
          <textarea
            id={field.id}
            ref={field.ref}
            name={field.name}
            className={styles.textarea}
            value={(field.value as string) ?? ''}
            onBlur={field.onBlur}
            onChange={(e) => field.onChange(e.target.value)}
            rows={4}
          />
        )}
      </FormField>
    </>
  );
}

export function WerfverslagCreateDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const createMutation = useCreateWerfverslag();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuw werfverslag</DialogTitle>
        <DialogDescription>
          Leg deelnemers en bespreekpunten van de werfvergadering vast.
        </DialogDescription>
        <DialogBody>
          <Form<WerfverslagFormValues>
            schema={schema}
            defaultValues={{
              date: new Date().toISOString().slice(0, 10),
              phaseId: defaultPhaseId ?? 'Ruw',
              attendeeContactIds: [],
              itemsCsv: '',
            }}
            onSubmit={async (values) => {
              const body: CreateWerfverslagCommand = {
                date: values.date,
                attendeeContactIds: values.attendeeContactIds,
                items: values.itemsCsv ? parseList(values.itemsCsv) : null,
                phaseId: values.phaseId,
              };
              await createMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Toevoegen</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function WerfverslagEditDialog({
  open,
  onOpenChange,
  werfverslag,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  werfverslag: WerfverslagDto;
}) {
  const updateMutation = useUpdateWerfverslag(werfverslag.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Werfverslag bewerken</DialogTitle>
        <DialogDescription>
          De backend laat alleen de lijst aanwezigen en items wijzigen.
        </DialogDescription>
        <DialogBody>
          <Form<WerfverslagFormValues>
            schema={schema}
            defaultValues={{
              date: (werfverslag.date ?? '').slice(0, 10),
              phaseId: werfverslag.phaseId,
              attendeeContactIds: werfverslag.attendees.map((a) => a.id),
              itemsCsv: werfverslag.items.join('\n'),
            }}
            onSubmit={async (values) => {
              const body: UpdateWerfverslagCommand = {
                attendeeContactIds: values.attendeeContactIds,
                items: values.itemsCsv ? parseList(values.itemsCsv) : null,
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Convert a werfverslag's action items into tasks via batchCreateTasks.
 * Each checked item maps to one task with the werfverslagId in the
 * backend command so the linkage stays bidirectional.
 */
export function BatchTaskDialog({
  open,
  onOpenChange,
  werfverslag,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  werfverslag: WerfverslagDto;
}) {
  const [checked, setChecked] = useState<Set<string>>(() => new Set(werfverslag.items));
  const batch = useBatchCreateTasks();

  const toggle = (item: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(item)) next.delete(item);
      else next.add(item);
      return next;
    });
  };

  const handleSubmit = async () => {
    const items = Array.from(checked);
    if (items.length === 0) return;
    await batch.mutateAsync({ werfverslagId: werfverslag.id, items });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Taken aanmaken uit werfverslag</DialogTitle>
        <DialogDescription>
          Selecteer welke items als taak moeten worden toegevoegd.
        </DialogDescription>
        <DialogBody>
          {werfverslag.items.length === 0 ? (
            <div className="mono c-ink-3">Dit werfverslag heeft geen besproken items.</div>
          ) : (
            <ul className={styles.itemList}>
              {werfverslag.items.map((item) => (
                <li key={item} className={styles.itemRow}>
                  <label className={styles.itemLabel}>
                    <input
                      type="checkbox"
                      checked={checked.has(item)}
                      onChange={() => toggle(item)}
                    />
                    <span>{item}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button" disabled={batch.isPending}>
              Annuleer
            </Btn>
          </DialogClose>
          <Btn
            variant="primary"
            onClick={handleSubmit}
            disabled={batch.isPending || checked.size === 0}
          >
            {batch.isPending ? 'Bezig…' : `Maak ${checked.size} taak/taken`}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
